import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { equipmentVendors } from '../data/a2zRentalCatalog';
import { shootingLocations } from '../data/shootingLocations';
import API_BASE_URL from '../config';
import './ProductionSourcingHubPage.css';

function normalize(s) {
  return String(s || '')
    .toLowerCase()
    .trim();
}

function vendorSearchBlob(v) {
  return [v.name, v.summary, v.address, v.city, v.region].filter(Boolean).join(' ');
}

function locationSearchBlob(loc) {
  return [loc.name, loc.summary, loc.city, loc.region].filter(Boolean).join(' ');
}

export default function ProductionSourcingHubPage() {
  const navigate = useNavigate();
  const [mode, setMode] = useState('gear');
  const [searchQuery, setSearchQuery] = useState('');
  const [locationQuery, setLocationQuery] = useState('');
  const [locationImageOverrides, setLocationImageOverrides] = useState({});
  const [statusMessage, setStatusMessage] = useState('');

  useEffect(() => {
    const fetchLocationImages = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/location-images`);
        const data = await res.json().catch(() => ({}));
        if (!res.ok) {
          console.error('Failed to fetch location images:', data.error);
          return;
        }
        const map = {};
        const images = data.images || {};
        Object.keys(images).forEach((key) => {
          if (images[key]?.url) map[key] = images[key].url;
        });
        setLocationImageOverrides(map);
      } catch (err) {
        console.error('Failed to load location images:', err);
      }
    };
    fetchLocationImages();
  }, []);

  const locationOptions = useMemo(() => {
    const set = new Set();
    const source = mode === 'gear' ? equipmentVendors : shootingLocations;
    source.forEach((item) => {
      if (item.city) set.add(item.city);
      if (item.region) set.add(item.region);
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [mode]);

  const filteredGear = useMemo(() => {
    const q = normalize(searchQuery);
    const loc = normalize(locationQuery);
    return equipmentVendors.filter((v) => {
      if (q) {
        const hay = normalize(vendorSearchBlob(v));
        if (!hay.includes(q)) return false;
      }
      if (loc) {
        const locHay = normalize([v.address, v.city, v.region].filter(Boolean).join(' '));
        if (!locHay.includes(loc)) return false;
      }
      return true;
    });
  }, [searchQuery, locationQuery]);

  const filteredLocations = useMemo(() => {
    const q = normalize(searchQuery);
    const loc = normalize(locationQuery);
    return shootingLocations.filter((item) => {
      if (q) {
        const hay = normalize(locationSearchBlob(item));
        if (!hay.includes(q)) return false;
      }
      if (loc) {
        const locHay = normalize([item.city, item.region].filter(Boolean).join(' '));
        if (!locHay.includes(loc)) return false;
      }
      return true;
    });
  }, [searchQuery, locationQuery]);

  const activeList = mode === 'gear' ? filteredGear : filteredLocations;
  const activeTotal = mode === 'gear' ? equipmentVendors.length : shootingLocations.length;

  const setModeAndClear = (next) => {
    setMode(next);
    setSearchQuery('');
    setLocationQuery('');
  };

  const handleUploadLocationImage = async (locId, file) => {
    if (!file) return;
    if (!String(file.type || '').startsWith('image/')) {
      setStatusMessage('Please upload an image file.');
      return;
    }
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setStatusMessage('Please login to upload location images.');
        navigate('/login', { state: { from: '/production-sourcing' } });
        return;
      }
      const formData = new FormData();
      formData.append('image', file);

      const res = await fetch(`${API_BASE_URL}/api/location-images/upload/${encodeURIComponent(locId)}`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        if (res.status === 401 || res.status === 403) {
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          setStatusMessage('Session expired. Please login again.');
          navigate('/login', { state: { from: '/production-sourcing' } });
          return;
        }
        if (res.status === 404) {
          setStatusMessage('Upload API not found on backend. Deploy latest backend changes and retry.');
          return;
        }
        const serverReason = [data.error, data.details].filter(Boolean).join(' - ');
        setStatusMessage(serverReason || `Failed to upload location image (HTTP ${res.status}).`);
        return;
      }
      setLocationImageOverrides((prev) => ({ ...prev, [locId]: data.url }));
      setStatusMessage('Location image uploaded successfully.');
    } catch (err) {
      console.error('Failed to upload location image:', err);
      setStatusMessage('Failed to upload location image.');
    }
  };

  const handleRemoveLocationImage = async (locId) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setStatusMessage('Please login to remove location images.');
        navigate('/login', { state: { from: '/production-sourcing' } });
        return;
      }
      const res = await fetch(`${API_BASE_URL}/api/location-images/${encodeURIComponent(locId)}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        if (res.status === 401 || res.status === 403) {
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          setStatusMessage('Session expired. Please login again.');
          navigate('/login', { state: { from: '/production-sourcing' } });
          return;
        }
        const serverReason = [data.error, data.details].filter(Boolean).join(' - ');
        setStatusMessage(serverReason || `Failed to remove location image (HTTP ${res.status}).`);
        return;
      }
      setLocationImageOverrides((prev) => {
        const next = { ...prev };
        delete next[locId];
        return next;
      });
      setStatusMessage('Location image removed.');
    } catch (err) {
      console.error('Failed to remove location image:', err);
      setStatusMessage('Failed to remove location image.');
    }
  };

  return (
    <div className="sourcing-hub">
      <div className="sourcing-hub-inner">
        <button type="button" className="sourcing-hub-back" onClick={() => navigate('/')}>
          ← Back to home
        </button>

        <h1>Production sourcing</h1>
        <p className="sourcing-hub-intro">
          {mode === 'gear'
            ? 'Choose a rental partner to view full equipment lists, categories, and daily rates.'
            : 'Browse studios and facilities available for shooting. Filter by name or city to shortlist locations.'}
        </p>

        <div className="sourcing-hub-mode" role="tablist" aria-label="Sourcing category">
          <button
            type="button"
            role="tab"
            aria-selected={mode === 'gear'}
            className={`sourcing-hub-mode-btn ${mode === 'gear' ? 'sourcing-hub-mode-btn--active' : ''}`}
            onClick={() => setModeAndClear('gear')}
          >
            Gear &amp; rentals
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={mode === 'locations'}
            className={`sourcing-hub-mode-btn ${mode === 'locations' ? 'sourcing-hub-mode-btn--active' : ''}`}
            onClick={() => setModeAndClear('locations')}
          >
            Shooting locations
          </button>
        </div>

        <div className="sourcing-hub-filters" role="search" aria-label={mode === 'gear' ? 'Filter suppliers' : 'Search locations'}>
          {statusMessage && <p className="sourcing-status-message">{statusMessage}</p>}
          <p className="sourcing-hub-filters-intro">
            {mode === 'gear'
              ? 'Advanced filtering options, including location-based search, enhance user convenience.'
              : 'Search by studio name or filter by city and region to find locations that fit your shoot.'}
          </p>
          <div className="sourcing-hub-filters-row">
            <label className="sourcing-hub-filter-field">
              <span className="sourcing-hub-filter-label">{mode === 'gear' ? 'Search' : 'Search locations'}</span>
              <input
                type="search"
                className="sourcing-hub-filter-input"
                placeholder={mode === 'gear' ? 'Name, gear, or keywords' : 'Studio or facility name'}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                autoComplete="off"
              />
            </label>
            <label className="sourcing-hub-filter-field">
              <span className="sourcing-hub-filter-label">Location</span>
              <input
                type="text"
                className="sourcing-hub-filter-input"
                placeholder="City or region (e.g. Hyderabad, Dubai)"
                value={locationQuery}
                onChange={(e) => setLocationQuery(e.target.value)}
                list="sourcing-hub-location-hints"
                autoComplete="off"
              />
              <datalist id="sourcing-hub-location-hints">
                {locationOptions.map((opt) => (
                  <option key={opt} value={opt} />
                ))}
              </datalist>
            </label>
          </div>
          {(searchQuery || locationQuery) && (
            <p className="sourcing-hub-filters-meta">
              Showing {activeList.length} of {activeTotal}{' '}
              {mode === 'gear' ? 'suppliers' : 'locations'}
              <button
                type="button"
                className="sourcing-hub-filters-clear"
                onClick={() => {
                  setSearchQuery('');
                  setLocationQuery('');
                }}
              >
                Clear filters
              </button>
            </p>
          )}
        </div>

        {mode === 'gear' ? (
          filteredGear.length === 0 ? (
            <p className="sourcing-hub-empty">No suppliers match your filters. Try different keywords or location.</p>
          ) : (
            <div className="sourcing-vendor-grid">
              {filteredGear.map((v) => {
                const digits = String(v.phone || '').replace(/\D/g, '');
                const telHref = digits ? `tel:+91${digits}` : null;
                return (
                  <button
                    key={v.id}
                    type="button"
                    className="sourcing-vendor-card"
                    onClick={() => navigate(v.catalogPath)}
                  >
                    <div className="sourcing-vendor-card-top">
                      <div className="sourcing-vendor-icon" aria-hidden>
                        {v.icon}
                      </div>
                      <div>
                        <h2>{v.name}</h2>
                        {(v.city || v.region) && (
                          <p className="sourcing-vendor-location">
                            {[v.city, v.region].filter(Boolean).join(', ')}
                          </p>
                        )}
                        <p className="sourcing-vendor-meta">{v.address}</p>
                        {telHref ? (
                          <p className="sourcing-vendor-phone">
                            <a href={telHref} onClick={(e) => e.stopPropagation()}>
                              {v.phone}
                            </a>
                          </p>
                        ) : (
                          <p className="sourcing-vendor-phone sourcing-vendor-phone--muted">Contact on supplier page</p>
                        )}
                      </div>
                    </div>
                    <p className="sourcing-vendor-summary">{v.summary}</p>
                    <p className="sourcing-vendor-cta">{v.cta || 'View details →'}</p>
                  </button>
                );
              })}
            </div>
          )
        ) : filteredLocations.length === 0 ? (
          <p className="sourcing-hub-empty">No locations match your filters. Try a different city or name.</p>
        ) : (
          <div className="sourcing-vendor-grid sourcing-vendor-grid--locations">
            {filteredLocations.map((loc) => (
              <article key={loc.id} className="sourcing-location-card">
                <div className="sourcing-location-photo-wrap">
                  {(locationImageOverrides[loc.id] || loc.imageUrl) ? (
                    <img
                      src={locationImageOverrides[loc.id] || loc.imageUrl}
                      alt={`${loc.name} location`}
                      className="sourcing-location-photo"
                      loading="lazy"
                    />
                  ) : (
                    <div className="sourcing-location-photo sourcing-location-photo--fallback" aria-hidden>
                      {loc.icon}
                    </div>
                  )}
                </div>
                <div className="sourcing-location-image-controls">
                  <label className="sourcing-location-upload-btn">
                    Upload picture
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => handleUploadLocationImage(loc.id, e.target.files?.[0])}
                    />
                  </label>
                  {locationImageOverrides[loc.id] && (
                    <button
                      type="button"
                      className="sourcing-location-remove-btn"
                      onClick={() => handleRemoveLocationImage(loc.id)}
                    >
                      Remove
                    </button>
                  )}
                </div>
                <div className="sourcing-vendor-card-top">
                  <div className="sourcing-vendor-icon sourcing-vendor-icon--location" aria-hidden>
                    {loc.icon}
                  </div>
                  <div>
                    <h2 className="sourcing-location-card-title">{loc.name}</h2>
                    <p className="sourcing-vendor-location">
                      {[loc.city, loc.region].filter(Boolean).join(', ')}
                    </p>
                  </div>
                </div>
                <p className="sourcing-vendor-summary">{loc.summary}</p>
                {loc.detailPath ? (
                  <button
                    type="button"
                    className="sourcing-location-view-btn"
                    onClick={() => navigate(loc.detailPath)}
                  >
                    {loc.cta || 'View services →'}
                  </button>
                ) : (
                  <p className="sourcing-location-card-foot">Available for production sourcing</p>
                )}
              </article>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
